import { useBrainStore } from '@/state/brainStore';
import type { BrainState } from '@/state/brainStore';
import { depositPaidAud } from '@/utils/bookingSummary';
import type { Deposit, DepositChannel, DepositLedgerEntry, StudioId } from '@/domain/types';

/**
 * Deposit reconciliation (amendment 11) — the deposit lane of the Reconcile screen.
 * Reconciles the money that ACTUALLY MOVED — each `paid` / `topup` ledger movement —
 * against its bank landing (`DepositLanding`), NOT the agreed/sent amount. A deposit
 * can be sent for one figure and paid in parts (initial + top-ups), so each movement
 * is its own row: card/transfer/link settle to the bank; cash sits in the till. The
 * agreed "sent" amount and any shortfall (sent − paid) ride along as context.
 *
 * Two failure modes the office chases by hand: a bank-bound movement with no landing
 * past the window ("in the system, not the bank"), and a landing matching no movement
 * ("in the bank, not the system"). Money-only: deposits with no paid/topup movement
 * (unpaid-sent, expired-unpaid) produce no rows — they live in the queue/CRM chase.
 * READ-ONLY over the store; provider-agnostic — nothing here names a card brand.
 */

/** Hours a bank-bound movement can sit with no landing before it is overdue. */
const OVERDUE_HOURS = 72;

export type DepositReconState = 'matched' | 'in-system-not-bank' | 'in-bank-not-system' | 'awaiting';

/** One row on the deposit lane: a single deposit money-movement, or an orphan bank landing. */
export interface DepositReconRow {
  /** Unique per movement (`${depositId}#${index}`) or the landing id for an orphan. */
  id: string;
  reference: string;
  /** Customer name (system side) or "Unmatched landing" for an orphan. */
  name: string;
  channel: DepositChannel;
  /** Which ledger movement this is: the initial payment or a later top-up. */
  movementKind: 'paid' | 'topup';
  /** The agreed/sent amount on the parent deposit — context, not the reconciled figure. */
  sentAud: number;
  /** The money that actually moved on this row (the ledger movement amount). */
  movedAud: number;
  /** Outstanding on the parent deposit (sent − total paid); 0 when fully paid. Context. */
  shortfallAud: number;
  /** Trading day the row belongs to: the movement's date, or the landing date. */
  dayIso: string;
  /** ISO date this movement's money landed in the bank — set on matched bank rows and orphan landings. */
  landedOn?: string;
  state: DepositReconState;
  /** Human reason, present on the two failure modes. */
  detail?: string;
}

export interface DepositReconciliation {
  studioId: StudioId;
  rows: DepositReconRow[];
  /** The two failure modes — the only rows that need chasing. */
  needsYou: DepositReconRow[];
  matched: DepositReconRow[];
  awaiting: DepositReconRow[];
}

/** Display channel for a system deposit, from how it was taken. */
export function depositChannel(d: Deposit): DepositChannel {
  if (d.method === 'cash') return 'cash';
  if (d.method === 'bank-transfer') return 'transfer';
  if (d.method === 'eftpos') return 'card';
  return 'link'; // provider-hosted link (no in-person method recorded)
}

function nameFor(brain: BrainState, d: Deposit): string {
  const c = brain.customers[d.customerId];
  return c ? `${c.firstName} ${c.lastName}` : (d.depositReference ?? '(deposit)');
}

const matchKey = (reference: string, amountAud: number): string => `${reference}::${amountAud}`;

/** One reconcilable money-movement pulled off a deposit's ledger (or synthesised). */
export interface DepositMovement {
  kind: 'paid' | 'topup';
  amountAud: number;
  /** ISO timestamp the money moved. */
  at: string;
  /** Where it went: card/transfer/link → bank; cash → till. */
  channel: DepositChannel;
}

/**
 * The money that actually moved on a deposit: its `paid` + `topup` ledger entries
 * (rollovers/transfers/forfeits/refunds are not fresh money — the same rule as
 * `depositPaidAud` and `depositsReceivedBreakdown`). A ledger-less deposit yields one
 * synthetic `paid` movement of `amountPaid ?? amountAud` (so the simple seeds behave
 * exactly as before); anything with nothing paid yields no movements. Each movement's
 * channel comes from its own entry, falling back to how the deposit was taken.
 */
export function depositMovements(d: Deposit): DepositMovement[] {
  const channelOf = (entry?: DepositLedgerEntry): DepositChannel => entry?.channel ?? depositChannel(d);
  if (d.ledger && d.ledger.length > 0) {
    return d.ledger
      .filter((e): e is DepositLedgerEntry & { kind: 'paid' | 'topup' } => e.kind === 'paid' || e.kind === 'topup')
      .map((e) => ({ kind: e.kind, amountAud: e.amountAud, at: e.at, channel: channelOf(e) }));
  }
  const paid = d.amountPaid ?? (d.status === 'paid' ? d.amountAud : 0);
  if (paid <= 0) return [];
  return [{ kind: 'paid', amountAud: paid, at: d.paidAt ?? d.createdAt, channel: depositChannel(d) }];
}

/**
 * Reconcile a studio's deposit money-movements against their bank landings as at
 * `nowIso`. Each `paid`/`topup` movement is a row: cash sits in the till; a bank-bound
 * movement matches a landing by reference + its own amount, else it is awaiting (recent)
 * or in-system-not-bank (past the window). Landings matching no movement are orphans
 * (in-bank-not-system). Money-only: deposits with no movement produce no rows.
 */
export function reconcileDeposits(studioId: StudioId, nowIso: string): DepositReconciliation {
  const brain = useBrainStore.getState();
  const now = new Date(nowIso).getTime();

  const deposits = Object.values(brain.deposits).filter((d) => d.studioId === studioId && d.depositReference);
  const landings = Object.values(brain.depositLandings).filter((l) => l.studioId === studioId);

  // A pool of bank landings, keyed by reference + amount; each is consumed by at most
  // one movement, so a landing left over at the end is a genuine orphan.
  const landingPool = new Map<string, typeof landings>();
  for (const l of landings) {
    const key = matchKey(l.reference, l.amountAud);
    const bucket = landingPool.get(key);
    if (bucket) bucket.push(l);
    else landingPool.set(key, [l]);
  }
  const takeLanding = (reference: string, amountAud: number) => {
    const bucket = landingPool.get(matchKey(reference, amountAud));
    return bucket && bucket.length > 0 ? bucket.shift() : undefined;
  };

  const rows: DepositReconRow[] = [];

  for (const d of deposits) {
    const reference = d.depositReference!;
    const name = nameFor(brain, d);
    const sentAud = d.amountAud;
    const shortfallAud = Math.max(0, sentAud - depositPaidAud(d));

    depositMovements(d).forEach((m, index) => {
      const base = {
        id: `${d.id}#${index}`,
        reference,
        name,
        channel: m.channel,
        movementKind: m.kind,
        sentAud,
        movedAud: m.amountAud,
        shortfallAud,
        dayIso: m.at.slice(0, 10),
      };

      if (m.channel === 'cash') {
        // Cash sits in the till — matched with no bank leg, so no landing date.
        rows.push({ ...base, state: 'matched' });
        return;
      }
      const landing = takeLanding(reference, m.amountAud);
      if (landing) {
        rows.push({ ...base, state: 'matched', landedOn: landing.landedOn.slice(0, 10) });
        return;
      }
      const ageHours = (now - new Date(m.at).getTime()) / 3_600_000;
      if (ageHours > OVERDUE_HOURS) {
        const days = Math.max(1, Math.round(ageHours / 24));
        rows.push({
          ...base,
          state: 'in-system-not-bank',
          detail: `Marked paid, no bank landing in ${days} day${days === 1 ? '' : 's'}.`,
        });
      } else {
        rows.push({ ...base, state: 'awaiting' });
      }
    });
  }

  for (const bucket of landingPool.values()) {
    for (const l of bucket) {
      rows.push({
        id: l.id,
        reference: l.reference,
        name: 'Unmatched landing',
        channel: l.channel,
        movementKind: 'paid',
        sentAud: l.amountAud,
        movedAud: l.amountAud,
        shortfallAud: 0,
        dayIso: l.landedOn.slice(0, 10),
        landedOn: l.landedOn.slice(0, 10),
        state: 'in-bank-not-system',
        detail: 'Landed in the bank, on no deposit record.',
      });
    }
  }

  return {
    studioId,
    rows,
    needsYou: rows.filter((r) => r.state === 'in-system-not-bank' || r.state === 'in-bank-not-system'),
    matched: rows.filter((r) => r.state === 'matched'),
    awaiting: rows.filter((r) => r.state === 'awaiting'),
  };
}
